/**
 * The command registry (plan §4.2, D2). Static catalog: scopes and commands are registered from
 * contribution files and packages. Dynamic part: components activate scope instances while mounted
 * (`useCommandScope`) and provide the context their commands run with. One window `keydown` listener
 * (`attach`) dispatches keys to the innermost matching command; menus, the command bar and buttons run
 * commands by id.
 */
import {
  type Platform,
  type ShortcutSpec,
  canonicalCombo,
  currentPlatform,
  isModifierKey,
  isPrintableKey,
  isGridTarget,
  isSingleKey,
  isTextTarget,
  matchesStroke,
  parseCombo,
  resolveShortcut,
  strokeCount,
} from '../keys';
import {
  type AnyCommand,
  type AnyScope,
  type CommandContribution,
  type CommandRunInfo,
  type CommandSource,
  SCOPE_LEVEL,
  type Scope,
  type ScopeActivation,
  type ScopeKind,
} from './types';

/** Where the registry learns about open layers (the `@dataroom/ui` layer stack in the app). */
export type LayerProbe = {
  /** The top-most open layer that is not suspended. */
  top(): { id: string; kind: string; modal: boolean } | undefined;
  /** The portal elements of a layer. */
  elements(id: string): Element[];
};

/** Layer kinds that do not take the keyboard from the page (tooltips, docked panels, drag previews). */
const NON_CAPTURING_LAYERS = new Set(['tooltip', 'panel', 'drag-overlay', 'inline']);

/** Sequences ('g h') wait this long for the next stroke. */
export const SEQUENCE_TIMEOUT_MS = 1000;

export type ActiveInstance = {
  readonly token: number;
  readonly scope: AnyScope;
  readonly activation: ScopeActivation<unknown>;
  readonly order: number;
};

export type ListedCommand = {
  command: AnyCommand;
  scope: AnyScope;
  /** Bindings on the current platform. */
  shortcuts: string[];
  enabled: boolean;
  /** Disabled now, but listed (muted) in the shortcut sheet: its `listed` predicate holds here. */
  listed?: boolean;
  /** The instance that would run it (absent when its scope is not active). */
  instance?: ActiveInstance;
};

const isDev = (() => {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
})();

/** Every binding of a command on a platform. */
export function commandShortcuts(command: AnyCommand, platform: Platform = currentPlatform()): string[] {
  if (!command.shortcut) return [];
  const specs: readonly ShortcutSpec[] =
    typeof command.shortcut === 'string' || !Array.isArray(command.shortcut)
      ? [command.shortcut as ShortcutSpec]
      : (command.shortcut as readonly ShortcutSpec[]);
  const out: string[] = [];
  for (const spec of specs) {
    const combo = resolveShortcut(spec, platform);
    if (combo && !out.includes(combo)) out.push(combo);
  }
  return out;
}

/** Default of `textSafe` (see `CommandDef.textSafe`). */
export function isTextSafe(command: AnyCommand, combo: string): boolean {
  if (command.textSafe !== undefined) return command.textSafe;
  if (isSingleKey(combo)) return false;
  const kind = command.scope.kind;
  return kind === 'global' || kind === 'route' || kind === 'layer';
}

export class CommandRegistry {
  private scopes = new Map<string, AnyScope>();
  private commands = new Map<string, AnyCommand>();
  private byScope = new Map<string, AnyCommand[]>();
  private instances: ActiveInstance[] = [];
  private listeners = new Set<() => void>();
  private seq = 0;
  private version = 0;
  private pending: {
    strokes: number;
    at: number;
    matches: Array<{ inst: ActiveInstance; cmd: AnyCommand; combo: string }>;
  } | null = null;
  private layers: LayerProbe | null = null;
  private detach: (() => void) | null = null;

  constructor(private readonly options: { strict?: boolean } = {}) {}

  // -- Catalog ------------------------------------------------------------------------------------

  /** Register scopes and commands. Duplicate ids or a binding repeated in one scope throw in development. */
  add(contribution: CommandContribution): () => void {
    const addedScopes: string[] = [];
    const addedCommands: string[] = [];
    for (const scope of contribution.scopes ?? []) {
      const existing = this.scopes.get(scope.id);
      if (existing && existing !== scope) this.fail(`Scope "${scope.id}" is defined twice`);
      if (!existing) {
        this.scopes.set(scope.id, scope);
        addedScopes.push(scope.id);
      }
    }
    for (const command of contribution.commands ?? []) {
      const existing = this.commands.get(command.id);
      if (existing) {
        if (existing !== command) this.fail(`Command "${command.id}" is registered twice`);
        continue;
      }
      if (!this.scopes.has(command.scope.id)) {
        this.scopes.set(command.scope.id, command.scope);
        addedScopes.push(command.scope.id);
      }
      for (const combo of commandShortcuts(command)) {
        parseCombo(combo); // throws on a malformed combo
        const canonical = canonicalCombo(combo);
        const clash = (this.byScope.get(command.scope.id) ?? []).find((other) =>
          commandShortcuts(other).some((c) => canonicalCombo(c) === canonical),
        );
        if (clash && !command.overrides?.includes(clash.id) && !clash.overrides?.includes(command.id))
          this.fail(
            `"${combo}" is bound twice in scope "${command.scope.id}": ${clash.id} and ${command.id}`,
          );
      }
      this.commands.set(command.id, command);
      const list = this.byScope.get(command.scope.id) ?? [];
      list.push(command);
      this.byScope.set(command.scope.id, list);
      addedCommands.push(command.id);
    }
    this.changed();
    return () => {
      for (const id of addedCommands) {
        const cmd = this.commands.get(id);
        this.commands.delete(id);
        if (cmd)
          this.byScope.set(
            cmd.scope.id,
            (this.byScope.get(cmd.scope.id) ?? []).filter((c) => c !== cmd),
          );
      }
      for (const id of addedScopes) if (!(this.byScope.get(id) ?? []).length) this.scopes.delete(id);
      this.changed();
    };
  }

  getCommand(id: string): AnyCommand | undefined {
    return this.commands.get(id);
  }

  getScope(id: string): AnyScope | undefined {
    return this.scopes.get(id);
  }

  allScopes(): AnyScope[] {
    return [...this.scopes.values()];
  }

  allCommands(): AnyCommand[] {
    return [...this.commands.values()];
  }

  commandsOf(scopeId: string): AnyCommand[] {
    return this.byScope.get(scopeId) ?? [];
  }

  // -- Active instances ---------------------------------------------------------------------------

  /** Activate a scope (while a component is mounted). Returns the deactivation. */
  activate<Ctx>(scope: Scope<Ctx>, activation: ScopeActivation<Ctx>): () => void {
    if (!this.scopes.has(scope.id)) this.scopes.set(scope.id, scope);
    this.seq += 1;
    const inst: ActiveInstance = {
      token: this.seq,
      scope,
      activation: activation as ScopeActivation<unknown>,
      order: this.seq,
    };
    this.instances.push(inst);
    this.changed();
    return () => {
      const i = this.instances.indexOf(inst);
      if (i >= 0) {
        this.instances.splice(i, 1);
        this.changed();
      }
    };
  }

  /** Layers decide whether page scopes may run (rule 3 of §4.2). */
  setLayerProbe(probe: LayerProbe | null): void {
    this.layers = probe;
  }

  /**
   * Active instances that apply to a key event on `target`, innermost first. Page scopes (route,
   * editor, grid, list) are skipped while a layer that takes the keyboard is open, unless the scope
   * lives in that layer.
   */
  stack(
    target: EventTarget | null = activeElement(),
    options: { ignoreLayers?: boolean } = {},
  ): ActiveInstance[] {
    const el = target instanceof Element ? target : null;
    const top = options.ignoreLayers ? undefined : this.layers?.top();
    const capturing = top && !NON_CAPTURING_LAYERS.has(top.kind) ? top : undefined;
    const topElements = capturing ? (this.layers?.elements(capturing.id) ?? []) : [];
    const inTopLayer = (node: Element | null) => Boolean(node && topElements.some((l) => l.contains(node)));
    const eligible: Array<{ inst: ActiveInstance; contains: boolean }> = [];
    for (const inst of this.instances) {
      const host = inst.activation.element?.() ?? null;
      const contains = Boolean(host && el && host.contains(el));
      if (inst.activation.focus === 'within' && !contains) continue;
      if (inst.activation.focus === 'within-or-idle' && !contains && !isIdleTarget(el)) continue;
      const kind = inst.scope.kind;
      if (capturing && kind !== 'global') {
        // A scope inside the top layer (its element or its layer id) stays live; page scopes pause.
        const own = inst.activation.layerId === capturing.id || (host !== null && inTopLayer(host));
        if (!own && !contains) continue;
      } else if (
        kind === 'layer' &&
        inst.activation.layerId &&
        top &&
        inst.activation.layerId !== top.id &&
        !contains
      )
        continue;
      eligible.push({ inst, contains });
    }
    eligible.sort(
      (a, b) =>
        SCOPE_LEVEL[b.inst.scope.kind] - SCOPE_LEVEL[a.inst.scope.kind] ||
        Number(b.contains) - Number(a.contains) ||
        b.inst.order - a.inst.order,
    );
    return eligible.map((e) => e.inst);
  }

  /** Is a modal layer (dialog, drawer, palette …) on top? Global single keys pause then. */
  private modalOnTop(): boolean {
    const top = this.layers?.top();
    return Boolean(top && !NON_CAPTURING_LAYERS.has(top.kind));
  }

  // -- Running ------------------------------------------------------------------------------------

  private instanceFor(
    command: AnyCommand,
    target: EventTarget | null = activeElement(),
  ): ActiveInstance | undefined {
    const stack = this.stack(target);
    return (
      stack.find((i) => i.scope.id === command.scope.id) ??
      // A menu opened from the scope (its popup lives in a layer) still runs the scope's command.
      [...this.instances].reverse().find((i) => i.scope.id === command.scope.id)
    );
  }

  /** Whether a command is active and enabled now. */
  canRun(id: string, target?: EventTarget | null): boolean {
    const command = this.commands.get(id);
    if (!command) return false;
    const inst = this.instanceFor(command, target);
    if (!inst) return false;
    return this.enabled(command, inst);
  }

  /** Run a command by id (menus, the command bar, buttons). Resolves false when it did not run. */
  run(id: string, source: CommandSource = 'api', target?: EventTarget | null): boolean {
    const command = this.commands.get(id);
    if (!command) return false;
    const inst = this.instanceFor(command, target);
    if (!inst || !this.enabled(command, inst)) return false;
    return this.invoke(command, inst, { source }) !== false;
  }

  private listed(command: AnyCommand, inst: ActiveInstance): boolean {
    try {
      if (command.listed === 'always') return true;
      return command.listed ? command.listed(inst.activation.ctx()) : false;
    } catch {
      return false;
    }
  }

  private enabled(command: AnyCommand, inst: ActiveInstance): boolean {
    try {
      return command.when ? command.when(inst.activation.ctx()) : true;
    } catch {
      return false;
    }
  }

  private invoke(command: AnyCommand, inst: ActiveInstance, info: CommandRunInfo): boolean {
    const result = command.run(inst.activation.ctx(), info);
    if (result instanceof Promise) {
      result.catch((err: unknown) => {
        console.error(`Command ${command.id} failed`, err);
      });
      return true;
    }
    return result !== false;
  }

  /** Guards of §4.2 rule 1 for one binding. */
  private guardAllows(
    command: AnyCommand,
    combo: string,
    inst: ActiveInstance,
    text: boolean,
    grid: boolean,
  ): boolean {
    const single = isSingleKey(combo);
    if (text && !isTextSafe(command, combo)) return false;
    if (single && grid && inst.scope.kind !== 'grid' && !command.gridSafe) return false;
    if (single && inst.scope.kind === 'global' && this.modalOnTop() && !command.textSafe) return false;
    return true;
  }

  /**
   * Handle a keydown. Returns true when a command ran (the event is then `preventDefault`ed). Events a
   * component already handled (`defaultPrevented`) and IME composition are ignored.
   */
  dispatch(event: KeyboardEvent): boolean {
    if (event.defaultPrevented || event.isComposing || isModifierKey(event)) return false;
    const target = event.target;
    const text = isTextTarget(target);
    const grid = isGridTarget(target);

    // Continue a sequence ('g' then 'h').
    if (this.pending) {
      const pending = this.pending;
      this.pending = null;
      if (event.timeStamp - pending.at <= SEQUENCE_TIMEOUT_MS) {
        const next: typeof pending.matches = [];
        for (const c of pending.matches) {
          if (!matchesStroke(event, c.combo, pending.strokes)) continue;
          if (pending.strokes + 1 === strokeCount(c.combo)) {
            if (!this.enabled(c.cmd, c.inst)) continue;
            if (this.invoke(c.cmd, c.inst, { source: 'keyboard', event })) {
              event.preventDefault();
              return true;
            }
          } else next.push(c);
        }
        if (next.length) {
          this.pending = { strokes: pending.strokes + 1, at: event.timeStamp, matches: next };
          event.preventDefault();
          return true;
        }
      }
    }

    const starts: Array<{ inst: ActiveInstance; cmd: AnyCommand; combo: string }> = [];
    for (const inst of this.stack(target)) {
      for (const cmd of this.commandsOf(inst.scope.id)) {
        for (const combo of commandShortcuts(cmd)) {
          if (!this.guardAllows(cmd, combo, inst, text, grid)) continue;
          if (!matchesStroke(event, combo, 0)) continue;
          if (strokeCount(combo) > 1) {
            starts.push({ inst, cmd, combo });
            continue;
          }
          if (event.repeat && !cmd.repeat) {
            // The command owns the key: swallow the repeat instead of letting it reach the page.
            event.preventDefault();
            return true;
          }
          if (!this.enabled(cmd, inst)) continue;
          if (this.invoke(cmd, inst, { source: 'keyboard', event })) {
            event.preventDefault();
            return true;
          }
        }
      }
    }
    if (starts.length && !event.repeat) {
      this.pending = { strokes: 1, at: event.timeStamp, matches: starts };
      event.preventDefault();
      return true;
    }
    return false;
  }

  /** Install the one window `keydown` listener (bubble phase: a component that handles a key wins). */
  attach(target: Window | Document = window): () => void {
    this.detach?.();
    const onKeyDown = (event: Event) => {
      this.dispatch(event as KeyboardEvent);
    };
    target.addEventListener('keydown', onKeyDown);
    this.detach = () => target.removeEventListener('keydown', onKeyDown);
    return () => {
      this.detach?.();
      this.detach = null;
    };
  }

  // -- Listing ------------------------------------------------------------------------------------

  /**
   * Commands of the active scopes, innermost scope first (the shortcut sheet, ⌘K). With `all`, every
   * registered command, active or not.
   */
  listCommands(
    options: {
      all?: boolean;
      target?: EventTarget | null;
      includeHidden?: boolean;
      /**
       * List as if no layer were open (the command bar is itself a layer: it lists what works where
       * the member was, with `target` = the element focused before it opened).
       */
      ignoreLayers?: boolean;
    } = {},
  ): ListedCommand[] {
    const out: ListedCommand[] = [];
    const seen = new Set<string>();
    const stack = this.stack(options.target === undefined ? activeElement() : options.target, {
      ignoreLayers: options.ignoreLayers,
    });
    for (const inst of stack) {
      for (const command of this.commandsOf(inst.scope.id)) {
        if (seen.has(command.id) || (command.hidden && !options.includeHidden)) continue;
        seen.add(command.id);
        const enabled = this.enabled(command, inst);
        out.push({
          command,
          scope: inst.scope,
          shortcuts: commandShortcuts(command),
          enabled,
          ...(!enabled && command.listed ? { listed: this.listed(command, inst) } : {}),
          instance: inst,
        });
      }
    }
    if (options.all)
      for (const command of this.commands.values()) {
        if (seen.has(command.id) || (command.hidden && !options.includeHidden)) continue;
        out.push({ command, scope: command.scope, shortcuts: commandShortcuts(command), enabled: false });
      }
    return out;
  }

  /** Active scope kinds, for diagnostics and tests. */
  activeScopes(): Array<{ id: string; kind: ScopeKind }> {
    return this.instances.map((i) => ({ id: i.scope.id, kind: i.scope.kind }));
  }

  // -- Subscription -------------------------------------------------------------------------------

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Changes on every registration or activation (for `useSyncExternalStore`). */
  getVersion(): number {
    return this.version;
  }

  private changed(): void {
    this.version += 1;
    for (const l of [...this.listeners]) l();
  }

  private fail(message: string): void {
    if (this.options.strict ?? isDev) throw new Error(message);
    console.warn(message);
  }

  /** Tests: forget everything. */
  reset(): void {
    this.scopes.clear();
    this.commands.clear();
    this.byScope.clear();
    this.instances = [];
    this.pending = null;
    this.changed();
  }
}

/** Nothing meaningful has focus: no target, or the document body / root. */
function isIdleTarget(el: Element | null): boolean {
  if (!el) return true;
  return typeof document !== 'undefined' && (el === document.body || el === document.documentElement);
}

function activeElement(): Element | null {
  return typeof document === 'undefined' ? null : document.activeElement;
}

/** Printable single keys that are allowed to be text-safe: none (they would swallow typing). */
export function isUnsafeTextBinding(command: AnyCommand, combo: string): boolean {
  if (!isSingleKey(combo)) return false;
  const strokes = parseCombo(combo);
  const printable = strokes.some((s) => isPrintableKey(s.key) || s.key === 'space');
  return printable && isTextSafe(command, combo);
}

/** The app's registry (one per page). */
export const commandRegistry = new CommandRegistry();
